<template>
  <div class="control">
    <!-- 模块列表 -->
    <ControlModels></ControlModels>
    <!-- editor 编辑面板 -->
    <ControlPanel></ControlPanel>
    <!-- 配置部分 -->
    <ControlConfigure></ControlConfigure>
  </div>
</template>

<script>
import ControlModels from "./components/ControlModels";
import ControlPanel from "./components/ControlPanel";
import ControlConfigure from "./components/ControlConfigure";
import { mapGetters, mapMutations } from "vuex";
import {
  getActivityAiText,
  saveActivityAiText,
  reinterpretActivityAiText,
  getActivityAiMergedText,
  saveActivityAiMergedText,
} from "@/api/activity";

const wait = (time) => new Promise((resolve) => setTimeout(resolve, time));
const normalizeImageUrl = (value) =>
  String(value || "")
    .trim()
    .replace(/^https?:/, "")
    .split(/[?#]/)[0];
const getField = (item, camelCaseKey, snakeCaseKey) =>
  item[camelCaseKey] !== undefined ? item[camelCaseKey] : item[snakeCaseKey];
const normalizeAiTextItem = (item = {}) => ({
  ...item,
  componentId: getField(item, "componentId", "component_id"),
  moduleName: getField(item, "moduleName", "module_name"),
  moduleNameResolved: getField(item, "moduleNameResolved", "module_name_resolved"),
  moduleNameSource: getField(item, "moduleNameSource", "module_name_source"),
  tabKey: getField(item, "tabKey", "tab_key"),
  tabName: getField(item, "tabName", "tab_name"),
  sortNo: getField(item, "sortNo", "sort_no"),
  imageUrl: getField(item, "imageUrl", "image_url"),
  aiText: getField(item, "aiText", "ai_text"),
  textSource: getField(item, "textSource", "text_source"),
  errorMsg: getField(item, "errorMsg", "error_msg"),
  retryCount: getField(item, "retryCount", "retry_count"),
  integrationBatchCount: getField(item, "integrationBatchCount", "integration_batch_count"),
  modifiedTime: getField(item, "modifiedTime", "modified_time"),
});
const normalizeAiMergedText = (item = {}) => ({
  ...item,
  activityUuid: item.activity_uuid,
  mergedText: item.merged_text,
  textSource: item.text_source,
  liveSliceCount: item.live_slice_count,
  mergedSliceCount: item.merged_slice_count,
  blankSliceCount: item.blank_slice_count,
  hasMaterial: item.has_material,
  mergeFormatVersion: item.merge_format_version,
  modifiedTime: item.modified_time,
});

export default {
  name: "Control",
  data() {
    return {
      dragstatus: false, //拖拽中
      curWidget: null, //当前拖拽的组件
      dragWidget: null, //
      aiTextComponents: {},
      aiTextActivity: null,
      aiMergedText: null,
      aiTextLoading: false,
      aiMergedTextLoading: false,
      aiComponentSaving: {},
      aiComponentGenerating: {},
      aiMergedTextSaving: false,
    };
  },
  provide() {
    return {
      control: this,
    };
  },
  mounted() {
    this.fetchAiText();
    if (!this.curWidget) this.fetchAiMergedText();
  },
  components: {
    ControlModels,
    ControlPanel,
    ControlConfigure,
  },
  computed: {
    ...mapGetters(["project"]),
  },
  watch: {
    curWidget(value, oldValue) {
      if (!value && oldValue) this.fetchAiMergedText();
    },
  },
  methods: {
    ...mapMutations(["setProject", "setDefaultProject"]),
    async fetchAiText(showLoading = true, componentId = "") {
      const uuid = this.$route.params.uuid;
      if (!uuid) return;

      if (showLoading) this.aiTextLoading = true;
      try {
        const params = componentId ? { componentId } : undefined;
        const res = await getActivityAiText(uuid, params);
        const components = (res.data && res.data.components) || [];
        if (componentId) {
          const record = components[0] ? normalizeAiTextItem(components[0]) : null;
          if (record) {
            this.$set(this.aiTextComponents, componentId, record);
          } else {
            this.$delete(this.aiTextComponents, componentId);
          }
        } else {
          this.aiTextComponents = components.reduce((result, item) => {
            const normalizedItem = normalizeAiTextItem(item);
            result[normalizedItem.componentId] = normalizedItem;
            return result;
          }, {});
        }
        this.aiTextActivity =
          res.data && res.data.activity ? normalizeAiTextItem(res.data.activity) : null;
      } catch (error) {
        return null;
      } finally {
        if (showLoading) this.aiTextLoading = false;
      }
    },
    async fetchAiMergedText() {
      const uuid = this.$route.params.uuid;
      if (!uuid) return;

      this.aiMergedTextLoading = true;
      try {
        const res = await getActivityAiMergedText(uuid);
        this.aiMergedText = res.data ? normalizeAiMergedText(res.data) : null;
      } catch (error) {
        return null;
      } finally {
        this.aiMergedTextLoading = false;
      }
    },
    async saveComponentAiText(componentId, aiText) {
      const uuid = this.$route.params.uuid;
      if (!uuid) return;

      this.$set(this.aiComponentSaving, componentId, true);
      try {
        await saveActivityAiText(uuid, [{ componentId, aiText }]);
        const currentRecord = this.aiTextComponents[componentId] || {};
        this.$set(this.aiTextComponents, componentId, {
          ...currentRecord,
          componentId,
          aiText,
        });
        this.$message.success("解读更新成功");
      } catch (error) {
        return null;
      } finally {
        this.$set(this.aiComponentSaving, componentId, false);
      }
    },
    async generateComponentAiText(componentId, imageUrl) {
      const uuid = this.$route.params.uuid;
      if (!uuid) return;

      this.$set(this.aiComponentGenerating, componentId, true);
      try {
        await reinterpretActivityAiText(uuid, { componentIds: [componentId] });
        this.$message.success("已提交生成任务");

        const result = await this.pollComponentAiText(componentId, imageUrl);
        if (result.success) {
          this.$message.success("解读生成成功");
        } else if (result.failed) {
          this.$message.error(result.errorMsg || "解读生成失败，请重试");
        } else {
          this.$message.info("解读仍在生成，可稍后查看");
        }
      } catch (error) {
        return null;
      } finally {
        this.$set(this.aiComponentGenerating, componentId, false);
      }
    },
    async pollComponentAiText(componentId, imageUrl) {
      for (let index = 0; index < 30; index++) {
        await wait(2000);
        await this.fetchAiText(false, componentId);
        const record = this.aiTextComponents[componentId];
        const isCurrentImage =
          record && normalizeImageUrl(record.imageUrl) === normalizeImageUrl(imageUrl);
        if (isCurrentImage && record.status === "success" && record.aiText) {
          return { success: true };
        }
        if (isCurrentImage && ["failed", "blocked", "cancelled"].includes(record.status)) {
          return { failed: true, errorMsg: record.errorMsg };
        }
      }
      return {};
    },
    async saveMergedAiText(aiText) {
      const uuid = this.$route.params.uuid;
      if (!uuid) return;

      this.aiMergedTextSaving = true;
      try {
        await saveActivityAiMergedText(uuid, { aiText });
        await this.fetchAiMergedText();
        this.$message.success("汇总解读更新成功");
      } catch (error) {
        return null;
      } finally {
        this.aiMergedTextSaving = false;
      }
    },
    refreshAiTextAfterSave() {
      return this.fetchAiMergedText();
    },
  },
};
</script>

<style lang="less" scoped>
.control {
  display: flex;
  width: 100%;
  height: calc(100vh - 56px);
  // background: #dedede;
  position: relative;
}
</style>
